import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const products = [
  {
    id: 'logistics',
    tag: 'Transport Management',
    title: 'Clutch Logistics',
    subtitle: 'Integrated Transport Management Solution',
    description:
      'Clutch Logistics combines a powerful web application with the Clutch Driver App to deliver an integrated logistics solution. Tailored for exporters and large-scale agricultural operations — manage assets, transport scheduling, and fleet monitoring from a single platform.',
    image: './images/clutch_logistics.png',
    link: '/logistics',
    linkLabel: 'Explore Logistics',
    features: ['Transport Scheduling', 'Fleet Monitoring', 'Driver App Integration', 'Web-Based Operations'],
    accentColor: '#0075FF',
  },
  {
    id: 'bookings',
    tag: 'Smart Scheduling',
    title: 'Clutch Booking',
    subtitle: 'Smart Scheduling Platform',
    description:
      'Clutch Booking is designed to optimise booking management for time-sensitive industries. Create customisable booking slots, track real-time availability, and improve scheduling efficiency — all seamlessly synced with your logistics operations.',
    image: './images/booking_img.png',
    link: '/bookings',
    linkLabel: 'Explore Bookings',
    features: ['Custom Booking Slots', 'Real-Time Availability', 'Integrated Scheduling', 'Seamless Communication'],
    accentColor: '#6366F1',
  },
  {
    id: 'driver',
    tag: 'Mobile Fleet',
    title: 'Clutch Driver App',
    subtitle: 'Mobile Fleet Management Tool',
    description:
      'Built for drivers in logistics and grain transport industries. The Clutch Driver App simplifies daily tasks, delivers real-time job assignments, and keeps drivers connected to dispatch teams — from pickup to delivery.',
    image: '/images/driver_app_prev.png',
    imageContain: true,
    link: '/logistics',
    linkLabel: 'Explore Logistics',
    features: ['Real-Time Job Assignments', 'Delivery Status Updates', 'GPS Route Tracking', 'Mobile-Friendly Interface'],
    accentColor: '#10B981',
    stores: true,
  },
  {
    id: 'ai-smb',
    tag: 'Built for Growth',
    title: 'AI for Small & Medium Businesses',
    subtitle: 'Enterprise-Grade AI, SMB-Friendly',
    description:
      'You do not need a massive budget or an in-house data team to benefit from AI. We help small and medium businesses identify high-impact opportunities, implement practical AI tools, and integrate them into everyday operations — from customer support chatbots to automated reporting.',
    link: '/contact',
    linkLabel: 'Get Started',
    features: ['AI Strategy & Consulting', 'Chatbots & Virtual Assistants', 'Process Automation', 'Affordable, Scalable Plans'],
    accentColor: '#F59E0B',
  },
  {
    id: 'ai-solutions',
    tag: 'Intelligent Automation',
    title: 'AI Solutions',
    subtitle: 'Custom AI That Fits Your Business',
    description:
      'From intelligent document processing to predictive analytics and workflow automation — we design and deploy AI solutions tailored to your industry. Leverage machine learning and modern AI tools to reduce manual work, surface insights, and drive smarter decisions.',
    link: '/contact',
    linkLabel: 'Discuss Your Project',
    features: ['Custom AI Development', 'Workflow Automation', 'Predictive Analytics', 'Document Intelligence'],
    accentColor: '#8B5CF6',
  },
];

const featureList = [
  'Transport Scheduling',
  'Fleet Monitoring',
  'Custom Booking Slots',
  'Real-Time Availability',
  'GPS Route Tracking',
  'Delivery Status Updates',
  'Driver App Integration',
  'Easy Reporting',
  'Web-Based Operations',
  'Compliance Management',
  'Asset Management',
  'Live Communication',
  'Custom AI Development',
  'Workflow Automation',
  'Predictive Analytics',
  'AI Strategy & Consulting',
  'Process Automation',
];

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98] } },
};


function ProductCard({ product, index, isSelected, onClick }) {
  return (
    <motion.div
      onClick={onClick}
      variants={itemVariants}
      whileHover={{ scale: 1.01 }}
      className={`relative rounded-2xl border cursor-pointer transition-all duration-300 overflow-hidden ${
        isSelected
          ? 'border-accent/30 shadow-card-hover bg-white'
          : 'border-border-subtle bg-white hover:border-border-medium hover:shadow-card'
      }`}
    >
      {/* Top accent line */}
      <div
        className="h-0.5 w-full transition-all duration-500"
        style={{ background: isSelected ? `linear-gradient(90deg, ${product.accentColor}, transparent)` : 'transparent' }}
      />

      <div className={`p-6 md:p-8 grid grid-cols-1 gap-6 items-start ${product.image ? 'lg:grid-cols-[1fr,auto]' : ''}`}>
        {/* Left: content */}
        <div>
          <span className="badge bg-accent/10 text-accent text-[10px] mb-4">
            {product.tag}
          </span>
          <h3 className="text-2xl font-bold text-primary mb-1">{product.title}</h3>
          <p className="text-secondary text-sm mb-4">{product.subtitle}</p>
          <p className="text-secondary leading-relaxed mb-6 text-sm md:text-base">{product.description}</p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-2 mb-6">
            {product.features.map((f) => (
              <span key={f} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/5 text-xs text-secondary border border-border-subtle">
                <svg className="w-3 h-3 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {f}
              </span>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-wrap gap-3">
            {product.link && (
              <Link
                to={product.link}
                onClick={(e) => e.stopPropagation()}
                className="btn-primary text-sm py-2.5"
              >
                {product.linkLabel}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                </svg>
              </Link>
            )}
            {product.stores && (
              <div className="flex gap-3 items-center">
                <a
                  href="https://apps.apple.com/us/app/clutch-driver-app/id6502280208"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="hover:opacity-80 transition-opacity"
                >
                  <img
                    src="https://toolbox.marketingtools.apple.com/api/v2/badges/download-on-the-app-store/black/en-us?releaseDate=1734825600"
                    alt="Download on App Store"
                    className="h-9 w-auto object-contain"
                  />
                </a>
                <button
                  onClick={(e) => { e.stopPropagation(); window.open('https://play.google.com/store/apps/details?id=au.com.clutchit.clutchdriverappv2', '_blank'); }}
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <img src="/images/google-play-badge.svg.webp" alt="Get on Google Play" className="h-9" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right: image */}
        {product.image && (
        <div className="hidden lg:block w-72 xl:w-80 flex-shrink-0">
          <div className="relative overflow-hidden aspect-[4/3]">
            <img src={product.image} alt={product.title} className={`w-full h-full ${product.imageContain ? 'object-contain p-4' : 'object-cover'}`} />
            <div className="absolute inset-0 bg-gradient-to-t from-surface-0/60 to-transparent" />
          </div>
        </div>
        )}
      </div>
    </motion.div>
  );
}

const Services = ({ compactTop = false }) => {
  const [selected, setSelected] = useState(0);

  return (
    <section className={`section-padding bg-surface-1 relative overflow-hidden ${compactTop ? '!pt-16' : ''}`}>
      {/* Decorative bg */}
      <div className="absolute inset-0 bg-mesh-grid pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-medium to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-medium to-transparent" />

      <div className="section-container relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="section-label">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
            </svg>
            Our Solutions
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            Technology<br />
            <span className="text-gradient-blue">Made Easy</span>
          </h2>
          <p className="text-secondary text-lg max-w-2xl mx-auto">
            Streamline logistics, automate with AI, and empower your team with
            purpose-built platforms for modern operations
          </p>
        </motion.div>

        {/* Scrolling feature pills */}
        <div className="relative overflow-hidden mb-12 py-3">
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-surface-1 to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-surface-1 to-transparent z-10 pointer-events-none" />
          <div className="flex gap-3 animate-slide-left w-max">
            {[...featureList, ...featureList].map((f, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-border-subtle text-secondary text-xs font-medium whitespace-nowrap flex-shrink-0 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                {f}
              </span>
            ))}
          </div>
        </div>

        {/* Product cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="flex flex-col gap-4"
        >
          {products.map((product, index) => (
            <ProductCard
              key={product.id}
              product={product}
              index={index}
              isSelected={selected === index}
              onClick={() => setSelected(index)}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
